import React, { useCallback, useMemo } from "react";
import useBusiness from "./graphql/useBusiness";
import useRootUser from "./graphql/useRootUser";
import { IntlContext } from "./IntlProviderWrapper";

const BusinessAppContext = React.createContext();

export const useBusinessContext = () => React.useContext(BusinessAppContext);

const DEFAULT_CURRENCY_CODE = "€";

function _swapSymbolsNeeded(value, decimalSeparator) {
  if (decimalSeparator === ",") {
    return value.replace(/[,.]/g, function ($1) {
      return $1 === "." ? "," : ".";
    });
  }
  return value;
}

const _editablePrice = (value, decimalSeparator) => {
  if (!value || value === "0E-6176") return "0";
  return _swapSymbolsNeeded(`${value}`, decimalSeparator);
};

function _currencyFormat(num, currencyCode, locale) {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    // TODO: compare with currency code
    useGrouping: true,
    currency: "EUR",
    minimumFractionDigits: 2,
  });
  if (isNaN(num)) {
    return `0,00 ${currencyCode}`;
  }
  return formatter.format(num);
}

export function BusinessAppProvider({ children }) {
  const [storeId, setStoreId] = React.useState();
  const [storeInfo, setStoreInfo] = React.useState();

  const { business, loading: loading1 } = useBusiness();
  const { rootUser, loading: loading2 } = useRootUser();

  const { locale } = React.useContext(IntlContext);

  const [editorLocale] = useMemo(() => {
    return [locale];
    /*
    // this will use browser language
    const navigatorLocale = navigator.language || navigator.userLanguage;
    if (!navigatorLocale) return "es";
    const navigatorLocaleData = navigatorLocale.split("-");
    return [navigatorLocaleData[0]];
     */
  }, [locale]);

  const currencyFormat = useCallback(
    (num) =>
      _currencyFormat(
        num,
        business?.currencyCode || DEFAULT_CURRENCY_CODE,
        editorLocale
      ),
    [business, editorLocale]
  );

  const handleKeyPressForPrices = useCallback(
    (event) => {
      const isSpanish = editorLocale === "es";
      const allowedKey = isSpanish ? "," : ".";
      if (!/[0-9]/.test(event.key) && event.key !== allowedKey) {
        event.preventDefault();
      }
    },
    [editorLocale]
  );

  const decimalSeparator = useMemo(
    () => (editorLocale === "en" ? "." : ","),
    [editorLocale]
  );

  const editablePrice = useCallback(
    (value) => {
      return _editablePrice(
        value,
        // TODO: improve this
        decimalSeparator
      );
    },
    [editorLocale, decimalSeparator]
  );

  const wrapped = useMemo(
    () => ({
      storeId,
      setStoreId,
      storeInfo,
      setStoreInfo,
      rootUser,
      business,
      currencyFormat,
      handleKeyPressForPrices,
      editablePrice,
      editorLocale,
      decimalSeparator,
      loading: loading1 || loading2,
    }),
    [
      loading1,
      loading2,
      storeId,
      storeInfo,
      rootUser,
      business,
      handleKeyPressForPrices,
      currencyFormat,
      editablePrice,
      decimalSeparator,
      editorLocale,
    ]
  );

  return (
    <BusinessAppContext.Provider value={wrapped}>
      {children}
    </BusinessAppContext.Provider>
  );
}
